import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Empty,
  Flex,
  Popconfirm,
  Tag,
  Typography
} from 'antd'
import type { TableColumnsType } from 'antd'
import { Monitor } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  type Session,
  organizationsAccessSessionsDestroy,
  organizationsAccessSessionsListOptions,
  organizationsAccessSessionsRevokeAllCreate
} from 'src/api/main'
import {
  ContentSpinner,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { DEFAULT_PER_PAGE_OPTIONS } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

const { Text } = Typography

type Props = {
  /** OrganizationAccess id (the member's access record), not the user id. */
  organizationAccessId: number
}

const locationOf = (s: Session): string =>
  [s.city, s.country_code].filter(Boolean).join(', ')

export const UserSessionsTab = ({ organizationAccessId }: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()

  const sessionsQuery = organizationsAccessSessionsListOptions({
    path: { id: organizationAccessId }
  })
  const { data, isLoading, isError, refetch } = useQuery({
    ...sessionsQuery,
    enabled: organizationAccessId > 0
  })

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: sessionsQuery.queryKey })

  const { mutateAsync: revokeOne, isPending: isRevoking } = useMutation({
    mutationFn: (jti: string) =>
      organizationsAccessSessionsDestroy({
        path: { id: organizationAccessId, jti },
        throwOnError: true
      }),
    onSuccess: () => {
      message.success(t('users.lifecycle.sessions.revoked'))
      void invalidate()
    },
    onError: (e) => showApiErrors(e)
  })

  const { mutateAsync: revokeAll, isPending: isRevokingAll } = useMutation({
    mutationFn: async () => {
      const { data: result } = await organizationsAccessSessionsRevokeAllCreate(
        {
          path: { id: organizationAccessId },
          throwOnError: true
        }
      )

      return result
    },
    onSuccess: (res) => {
      message.success(
        t('users.lifecycle.sessions.revokedAll', { count: res.revoked })
      )
      void invalidate()
    },
    onError: (e) => showApiErrors(e)
  })

  const sessions = data ?? []

  const columns: TableColumnsType<Session> = [
    {
      title: t('users.lifecycle.sessions.columns.device'),
      key: 'device',
      fixed: 'left',
      render: (_, s) => (
        <Flex align="center" gap={8}>
          <Monitor
            size={14}
            style={{ color: 'var(--ant-color-text-tertiary)' }}
          />
          <Flex vertical style={{ lineHeight: 1.3, minWidth: 0 }}>
            <Text style={{ fontSize: 13 }} ellipsis={{ tooltip: s.user_agent }}>
              {s.device_name || t('users.lifecycle.sessions.unknownDevice')}
            </Text>
            {s.is_current ? (
              <Tag color="success" style={{ marginInlineEnd: 0, fontSize: 10 }}>
                {t('users.lifecycle.sessions.current')}
              </Tag>
            ) : null}
          </Flex>
        </Flex>
      )
    },
    {
      title: t('users.lifecycle.sessions.columns.location'),
      key: 'location',
      width: 150,
      render: (_, s) => {
        const loc = locationOf(s)

        return loc ? (
          <Text style={{ fontSize: 12 }}>{loc}</Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        )
      }
    },
    {
      title: t('users.lifecycle.sessions.columns.ip'),
      key: 'ip',
      width: 140,
      render: (_, s) => (
        <Text
          style={{ fontSize: 12 }}
          type={s.ip_address ? undefined : 'secondary'}
        >
          {s.ip_address || '—'}
        </Text>
      )
    },
    {
      title: t('users.lifecycle.sessions.columns.lastActive'),
      key: 'lastActive',
      width: 140,
      render: (_, s) => (
        <DateTimeCell
          value={s.last_used_at}
          variant="relative"
          style={{ fontSize: 12 }}
        />
      )
    },
    {
      title: t('users.lifecycle.sessions.columns.created'),
      key: 'created',
      width: 130,
      render: (_, s) => (
        <DateTimeCell
          value={s.created_at}
          variant="date"
          style={{ fontSize: 12 }}
        />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 90,
      align: 'right' as const,
      render: (_, s) => (
        <Popconfirm
          title={t('users.lifecycle.sessions.revokeConfirm')}
          okText={t('users.lifecycle.sessions.revoke')}
          okButtonProps={{ danger: true }}
          onConfirm={() => void revokeOne(s.id)}
        >
          <Button size="small" danger disabled={isRevoking}>
            {t('users.lifecycle.sessions.revoke')}
          </Button>
        </Popconfirm>
      )
    }
  ]

  if (isLoading) {
    return (
      <Card>
        <ContentSpinner minHeight={240} />
      </Card>
    )
  }
  if (isError) {
    return (
      <Card>
        <QueryErrorAlert onRetry={() => void refetch()} />
      </Card>
    )
  }

  return (
    <ProTableContainer<Session>
      showSearch={false}
      columns={columns}
      dataSource={sessions}
      rowKey="id"
      pagination={{
        defaultPageSize: 25,
        pageSizeOptions: DEFAULT_PER_PAGE_OPTIONS.map(String),
        showSizeChanger: { showSearch: false },
        size: 'small',
        hideOnSinglePage: true,
        showTotal: (count, [from, to]) =>
          t('pagination.range', {
            from,
            to,
            total: count,
            defaultValue: `${from}-${to} of ${count}`
          })
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('users.lifecycle.sessions.empty')}
              </Text>
            }
            style={{ padding: '32px 0' }}
          />
        )
      }}
      leftChildren={
        <Flex vertical style={{ lineHeight: 1.25 }}>
          <Text strong style={{ fontSize: 13 }}>
            {t('users.lifecycle.sessions.title')}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t('users.lifecycle.sessions.subtitle')}
          </Text>
        </Flex>
      }
      rightChildren={
        sessions.length > 0 ? (
          <Popconfirm
            title={t('users.lifecycle.sessions.revokeAllConfirm')}
            okText={t('users.lifecycle.sessions.revokeAll')}
            okButtonProps={{ danger: true }}
            onConfirm={() => void revokeAll()}
          >
            <Button size="small" danger loading={isRevokingAll}>
              {t('users.lifecycle.sessions.revokeAll')}
            </Button>
          </Popconfirm>
        ) : null
      }
    />
  )
}
